import React, { useState, useEffect, useRef, useCallback } from 'react';
import { database } from '../firebase';
import { ref, get, set } from 'firebase/database';
import {
  Mic,
  MicOff,
  X,
  Brain,
  Volume2,
  Wifi,
  WifiOff,
  Database
} from 'lucide-react';
import './GlobalAIAgent.css';

const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;
const REALTIME_MODEL = 'gpt-4o-mini-realtime-preview-2024-12-17';

// ============================================
// MEMORY HIGHWAY SYSTEM
// ============================================

class MemoryHighway {
  constructor(userId, projects) {
    this.userId = userId;
    this.projects = projects;

    // Memory Banks
    this.STM = {           // Short-Term Memory Cache
      activeGoals: [],
      currentConstraints: [],
      recentDecisions: [],
      openThreads: [],
      lastRefresh: null
    };
    this.PMB = [];         // Personal Memory Bank
    this.ECB = {};         // Entity/Company Banks (per project)
    this.CL = [];          // Conversation Log

    this.loaded = false;
  }

  // HIGHWAY 0: Load all memory banks from Firebase
  async loadFromFirebase() {
    if (!this.userId) return;

    try {
      // Load Personal Memory Bank
      const pmbRef = ref(database, `users/${this.userId}/personalProfileBank`);
      const pmbSnap = await get(pmbRef);
      this.PMB = pmbSnap.val() || [];

      // Load Entity Banks for each project
      for (const project of this.projects) {
        const ecbRef = ref(database, `users/${this.userId}/projects/${project.id}/dataBank`);
        const ecbSnap = await get(ecbRef);
        this.ECB[project.id] = ecbSnap.val() || [];
      }

      // Load Conversation Log
      const clRef = ref(database, `users/${this.userId}/conversationLog`);
      const clSnap = await get(clRef);
      this.CL = clSnap.val() || [];

      this.loaded = true;
      this.refreshSTM();
    } catch (e) {
      console.error('Failed to load memory banks:', e);
    }
  }

  // HIGHWAY 1: Refresh Short-Term Memory from recent data
  refreshSTM() {
    const now = Date.now();
    const recentThreshold = 7 * 24 * 60 * 60 * 1000; // 7 days

    // Collect active goals from all projects
    this.STM.activeGoals = this.projects.flatMap(p =>
      (p.goals || [])
        .filter(g => !g.completed)
        .map(g => ({ ...g, projectId: p.id, projectName: p.name }))
    ).slice(0, 10);

    // Collect recent decisions from PMB
    this.STM.recentDecisions = this.PMB
      .filter(m => m.insights?.some(i => i.type === 'decision'))
      .slice(-5)
      .map(m => m.insights.find(i => i.type === 'decision')?.text);

    // Collect open threads from conversation log
    this.STM.openThreads = this.CL
      .filter(c => now - c.timestamp < recentThreshold)
      .slice(-10)
      .map(c => ({ role: c.role, summary: c.content?.slice(0, 100) }));

    // Collect constraints/preferences from PMB
    this.STM.currentConstraints = this.PMB
      .filter(m => m.insights?.some(i => i.type === 'preference'))
      .slice(-5)
      .map(m => m.insights.find(i => i.type === 'preference')?.text);

    this.STM.lastRefresh = now;
  }

  // HIGHWAY 0: Append to Conversation Log (immutable)
  async appendToConversationLog(role, content) {
    const entry = {
      role,
      content,
      timestamp: Date.now(),
      turnIndex: this.CL.length
    };

    this.CL.push(entry);

    // Persist to Firebase
    if (this.userId) {
      try {
        await set(ref(database, `users/${this.userId}/conversationLog`), this.CL);
      } catch (e) {
        console.error('Failed to save conversation log:', e);
      }
    }

    return entry;
  }

  // HIGHWAY 2: Write to Personal Memory Bank (distilled only)
  async writeToPersonalMemory(candidate) {
    if (!this.qualifyMemoryCandidate(candidate)) return false;

    const entry = {
      type: candidate.type,
      summary: candidate.summary,
      insights: candidate.insights || [],
      sourceTimestamp: Date.now(),
      sourceTurn: this.CL.length - 1
    };

    this.PMB.push(entry);

    // Persist to Firebase
    if (this.userId) {
      try {
        await set(ref(database, `users/${this.userId}/personalProfileBank`), this.PMB);
      } catch (e) {
        console.error('Failed to save PMB:', e);
      }
    }

    this.refreshSTM();
    return true;
  }

  // HIGHWAY 3: Write to Entity Memory Bank (distilled only)
  async writeToEntityMemory(entityId, candidate) {
    if (!entityId || !this.qualifyMemoryCandidate(candidate)) return false;

    if (!this.ECB[entityId]) {
      this.ECB[entityId] = [];
    }

    const entry = {
      type: candidate.type,
      data: candidate.data,
      summary: candidate.summary,
      timestamp: Date.now(),
      sourceTurn: this.CL.length - 1,
      aiContext: candidate.aiContext
    };

    this.ECB[entityId].push(entry);

    // Persist to Firebase
    if (this.userId) {
      try {
        await set(ref(database, `users/${this.userId}/projects/${entityId}/dataBank`), this.ECB[entityId]);
      } catch (e) {
        console.error('Failed to save ECB:', e);
      }
    }

    this.refreshSTM();
    return true;
  }

  // Memory Quality Gate
  qualifyMemoryCandidate(candidate) {
    if (!candidate) return false;
    if (!candidate.summary && !candidate.data) return false;
    if (candidate.summary?.length < 10) return false;
    return true;
  }

  // HIGHWAY 4: Build context for AI from STM + targeted lookups
  buildContextForAI() {
    const context = {
      // From STM
      activeGoals: this.STM.activeGoals.map(g =>
        `[${g.projectName}] ${g.title}${g.completed ? ' (done)' : ''}`
      ),
      recentDecisions: this.STM.recentDecisions.filter(Boolean),
      userPreferences: this.STM.currentConstraints.filter(Boolean),

      // Project summaries
      projectSummaries: this.projects.map(p => ({
        id: p.id,
        name: p.name,
        goalsCount: p.goals?.length || 0,
        completedGoals: p.goals?.filter(g => g.completed).length || 0,
        tasksCount: p.todos?.length || 0,
        completedTasks: p.todos?.filter(t => t.completed).length || 0
      })),

      // Recent conversation context
      recentConversation: this.CL.slice(-5).map(c =>
        `${c.role}: ${c.content?.slice(0, 150)}`
      )
    };

    return context;
  }

  // Get entity-specific context
  getEntityContext(entityId) {
    const bank = this.ECB[entityId] || [];
    return bank.slice(-20).map(e => ({
      type: e.type,
      summary: e.summary || e.data?.title || JSON.stringify(e.data).slice(0, 100)
    }));
  }

  // HIGHWAY 4b: Get comprehensive project knowledge from Entity Memory Bank
  getProjectKnowledge(projectName) {
    // Find the project
    const project = this.projects.find(p =>
      p.name.toLowerCase() === projectName.toLowerCase()
    );

    if (!project) {
      return {
        found: false,
        summary: `I don't have a project called "${projectName}" in my memory.`
      };
    }

    const entityBank = this.ECB[project.id] || [];

    // Analyze the entity bank events
    const eventCounts = {};
    const recentEvents = [];
    const completedItems = [];
    const addedItems = [];
    const decisions = [];

    entityBank.forEach(event => {
      // Count event types
      eventCounts[event.type] = (eventCounts[event.type] || 0) + 1;

      // Categorize events
      if (event.type.includes('COMPLETED')) {
        completedItems.push({
          item: event.aiContext?.item || event.data?.title || event.data?.task,
          when: event.timestamp
        });
      } else if (event.type.includes('CREATED') || event.type.includes('ADDED')) {
        addedItems.push({
          item: event.aiContext?.item || event.data?.title || event.data?.task,
          when: event.timestamp
        });
      } else if (event.type === 'AI_QUESTION_ANSWERED') {
        decisions.push({
          question: event.data?.question,
          answer: event.data?.answer
        });
      }

      // Track recent events (last 10)
      if (recentEvents.length < 10) {
        recentEvents.push({
          type: event.type,
          item: event.aiContext?.item || event.summary,
          when: event.timestamp
        });
      }
    });

    // Sort recent events by time (newest first)
    recentEvents.sort((a, b) => b.when - a.when);

    // Build knowledge summary
    const goalsCount = project.goals?.length || 0;
    const completedGoals = project.goals?.filter(g => g.completed).length || 0;
    const tasksCount = project.todos?.length || 0;
    const completedTasks = project.todos?.filter(t => t.completed).length || 0;

    // Format time helper
    const formatTimeAgo = (ts) => {
      if (!ts) return '';
      const diff = Date.now() - ts;
      const days = Math.floor(diff / 86400000);
      if (days > 0) return `${days} days ago`;
      const hours = Math.floor(diff / 3600000);
      if (hours > 0) return `${hours} hours ago`;
      return 'recently';
    };

    // Build human-readable summary
    let summary = `Here's what I know about ${project.name}:\n\n`;

    // Current status
    summary += `📊 Current Status:\n`;
    summary += `- ${completedGoals} of ${goalsCount} objectives completed\n`;
    summary += `- ${completedTasks} of ${tasksCount} tasks done\n`;
    summary += `- ${entityBank.length} total events recorded in the Entity Memory Bank\n\n`;

    // Active objectives
    const activeGoals = project.goals?.filter(g => !g.completed) || [];
    if (activeGoals.length > 0) {
      summary += `🎯 Active Objectives:\n`;
      activeGoals.slice(0, 5).forEach(g => {
        summary += `- ${g.title}\n`;
      });
      summary += '\n';
    }

    // Recent activity
    if (recentEvents.length > 0) {
      summary += `📜 Recent Activity:\n`;
      recentEvents.slice(0, 5).forEach(e => {
        const ago = formatTimeAgo(e.when);
        summary += `- ${e.type.replace(/_/g, ' ')}: ${e.item}${ago ? ` (${ago})` : ''}\n`;
      });
      summary += '\n';
    }

    // Key decisions from AI questions
    if (decisions.length > 0) {
      summary += `💡 Key Decisions Recorded:\n`;
      decisions.slice(0, 3).forEach(d => {
        summary += `- Q: ${d.question}\n  A: ${d.answer}\n`;
      });
      summary += '\n';
    }

    // Completed items summary
    if (completedItems.length > 0) {
      summary += `✅ Recently Completed:\n`;
      completedItems.slice(-5).reverse().forEach(c => {
        summary += `- ${c.item}\n`;
      });
    }

    return {
      found: true,
      projectId: project.id,
      projectName: project.name,
      summary,
      stats: {
        totalEvents: entityBank.length,
        goalsCount,
        completedGoals,
        tasksCount,
        completedTasks,
        eventCounts
      },
      recentEvents,
      decisions
    };
  }
}

// ============================================
// TOOL DEFINITIONS FOR REALTIME API
// ============================================

const AGENT_TOOLS = [
  {
    type: 'function',
    name: 'add_goal',
    description: 'Add a new OBJECTIVE/GOAL to a project. Use this for high-level outcomes, deliverables, or major milestones. Examples: "Launch MVP", "Complete pilot program", "Develop proposal document". When the user says "objective", "goal", "milestone", or describes a major outcome, use this tool.',
    parameters: {
      type: 'object',
      properties: {
        project_name: {
          type: 'string',
          description: 'The name of the project to add the objective to'
        },
        goal_title: {
          type: 'string',
          description: 'The title of the new objective/goal'
        }
      },
      required: ['project_name', 'goal_title']
    }
  },
  {
    type: 'function',
    name: 'add_task',
    description: 'Add a small actionable TASK that supports a specific objective. Tasks MUST be linked to an objective. If no objective exists, create one first using add_goal. Examples: "Email the team", "Review document", "Schedule meeting".',
    parameters: {
      type: 'object',
      properties: {
        project_name: {
          type: 'string',
          description: 'The name of the project'
        },
        objective_title: {
          type: 'string',
          description: 'The objective this task supports. If the project has no objectives, you MUST create one first using add_goal.'
        },
        task_description: {
          type: 'string',
          description: 'The description of the specific task/action'
        }
      },
      required: ['project_name', 'objective_title', 'task_description']
    }
  },
  {
    type: 'function',
    name: 'complete_goal',
    description: 'Mark a goal as completed',
    parameters: {
      type: 'object',
      properties: {
        project_name: { type: 'string' },
        goal_title: { type: 'string' }
      },
      required: ['project_name', 'goal_title']
    }
  },
  {
    type: 'function',
    name: 'complete_task',
    description: 'Mark a task as completed',
    parameters: {
      type: 'object',
      properties: {
        project_name: { type: 'string' },
        task_description: { type: 'string' }
      },
      required: ['project_name', 'task_description']
    }
  },
  {
    type: 'function',
    name: 'navigate_to_project',
    description: 'Navigate the UI to show a specific project',
    parameters: {
      type: 'object',
      properties: {
        project_name: { type: 'string' }
      },
      required: ['project_name']
    }
  },
  {
    type: 'function',
    name: 'get_project_status',
    description: 'Get detailed status of a specific project',
    parameters: {
      type: 'object',
      properties: {
        project_name: { type: 'string' }
      },
      required: ['project_name']
    }
  },
  {
    type: 'function',
    name: 'store_user_preference',
    description: 'Store a user preference or personal information to memory',
    parameters: {
      type: 'object',
      properties: {
        preference_type: {
          type: 'string',
          enum: ['preference', 'goal', 'constraint', 'decision']
        },
        content: { type: 'string' }
      },
      required: ['preference_type', 'content']
    }
  },
  {
    type: 'function',
    name: 'go_home',
    description: 'Navigate back to the main globe/overview screen',
    parameters: { type: 'object', properties: {} }
  },
  {
    type: 'function',
    name: 'get_project_knowledge',
    description: 'Query what is known about a project from the Entity Memory Bank. Use this when the user asks "what do you know about [project]" or wants a summary of project history, decisions, and activities.',
    parameters: {
      type: 'object',
      properties: {
        project_name: {
          type: 'string',
          description: 'The name of the project to get knowledge about'
        }
      },
      required: ['project_name']
    }
  },
  {
    type: 'function',
    name: 'store_objective_context',
    description: 'Store key context information about an objective to help the user succeed. Use this after adding a goal OR when the user provides important details about an objective like: deadline, stakeholders, success criteria, constraints, or key requirements.',
    parameters: {
      type: 'object',
      properties: {
        project_name: {
          type: 'string',
          description: 'The project name'
        },
        objective_title: {
          type: 'string',
          description: 'The objective this context relates to'
        },
        context_type: {
          type: 'string',
          enum: ['deadline', 'stakeholder', 'success_criteria', 'constraint', 'requirement', 'resource', 'risk', 'note'],
          description: 'Type of context information'
        },
        context_value: {
          type: 'string',
          description: 'The context information to store'
        }
      },
      required: ['project_name', 'objective_title', 'context_type', 'context_value']
    }
  },
  {
    type: 'function',
    name: 'get_objective_debrief',
    description: 'Get a complete debrief/summary of an objective including all stored context. Use this when the user asks to review an objective, wants a debrief, or asks "what do I need to know about [objective]".',
    parameters: {
      type: 'object',
      properties: {
        project_name: {
          type: 'string',
          description: 'The project name'
        },
        objective_title: {
          type: 'string',
          description: 'The objective to debrief (optional - if not provided, debriefs all active objectives)'
        }
      },
      required: ['project_name']
    }
  },
  {
    type: 'function',
    name: 'show_visual',
    description: 'DISPLAY A VISUAL ON SCREEN. ALWAYS include all 3 parameters: visual_type, title, AND data.',
    parameters: {
      type: 'object',
      properties: {
        visual_type: {
          type: 'string',
          enum: ['table', 'list', 'flowchart', 'timeline', 'metrics', 'comparison', 'mindmap', 'sketch'],
          description: 'The visual type'
        },
        title: {
          type: 'string',
          description: 'Title shown above the visual'
        },
        data: {
          type: 'object',
          description: 'REQUIRED. The content to display. Example for flowchart: {"nodes":[{"id":"1","label":"Step 1"},{"id":"2","label":"Step 2"}]}. Example for list: {"items":["Item 1","Item 2"]}. Example for sketch: {"elements":[{"type":"circle","x":200,"y":150,"r":50}]}'
        }
      },
      required: ['visual_type', 'title', 'data']
    }
  },
  {
    type: 'function',
    name: 'clear_visual',
    description: 'Remove the current visual from the screen',
    parameters: {
      type: 'object',
      properties: {}
    }
  }
];

// ============================================
// MAIN COMPONENT
// ============================================

const GlobalAIAgent = ({ userId, projects, onNavigate, currentView, currentProject, onProjectUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [messages, setMessages] = useState([]);
  const [memoryStatus, setMemoryStatus] = useState('loading');

  // Immersive speaking mode state
  const [immersiveMode, setImmersiveMode] = useState(false);
  const [immersiveExiting, setImmersiveExiting] = useState(false);
  const [sentences, setSentences] = useState([]);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');

  // Visual aids state
  const [currentVisual, setCurrentVisual] = useState(null);
  const [_visualHistory, setVisualHistory] = useState([]); // eslint-disable-line no-unused-vars

  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const processorRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const memoryRef = useRef(null);
  const sentenceTimerRef = useRef(null);
  const fullResponseRef = useRef('');
  const immersiveModeRef = useRef(false);

  // Queue system for paced sentence display
  const sentenceQueueRef = useRef([]);
  const isAdvancingRef = useRef(false);
  const lastSentenceCountRef = useRef(0);

  // Parse only complete sentences from text
  const parseCompleteSentences = (text) => {
    if (!text) return [];
    const matches = text.match(/[^.!?]*[.!?]+/g) || [];
    return matches.map(s => s.trim()).filter(s => s.length > 5);
  };

  // Calculate speaking time for a sentence (approx 150 words per minute)
  const getSpeakingTime = (sentence) => {
    const words = sentence.split(/\s+/).length;
    const baseTime = (words / 150) * 60 * 1000; // ms
    return Math.max(3000, Math.min(baseTime + 1500, 10000)); // 3-10 seconds
  };

  // Advance to next sentence in queue
  const advanceToNextSentence = useCallback(() => {
    if (sentenceQueueRef.current.length === 0) {
      isAdvancingRef.current = false;
      return;
    }

    isAdvancingRef.current = true;
    const nextSentence = sentenceQueueRef.current.shift();

    setDisplayedText(nextSentence);
    setCurrentSentenceIndex(prev => prev + 1);

    // Schedule next advancement based on sentence length
    const displayTime = getSpeakingTime(nextSentence);
    sentenceTimerRef.current = setTimeout(() => {
      if (sentenceQueueRef.current.length > 0) {
        advanceToNextSentence();
      } else {
        isAdvancingRef.current = false;
      }
    }, displayTime);
  }, []);

  // End immersive mode - clean exit without blinking
  const endImmersiveMode = useCallback(() => {
    // Immediate cleanup - no animation to avoid blinking
    setImmersiveMode(false);
    setImmersiveExiting(false);
    immersiveModeRef.current = false;
    setSentences([]);
    setCurrentSentenceIndex(0);
    setDisplayedText('');
    setCurrentVisual(null);
    setVisualHistory([]);
    sentenceQueueRef.current = [];
    lastSentenceCountRef.current = 0;
    isAdvancingRef.current = false;
    if (sentenceTimerRef.current) {
      clearTimeout(sentenceTimerRef.current);
      sentenceTimerRef.current = null;
    }
  }, []);

  // Initialize Memory Highway
  useEffect(() => {
    if (!userId || !projects) return;

    const initMemory = async () => {
      setMemoryStatus('loading');
      memoryRef.current = new MemoryHighway(userId, projects);
      await memoryRef.current.loadFromFirebase();
      setMemoryStatus('ready');
    };

    initMemory();
  }, [userId, projects]);

  // Build system prompt with memory context
  const buildSystemPrompt = useCallback(() => {
    if (!memoryRef.current?.loaded) {
      return 'You are Delta AI, a voice assistant. Memory is still loading.';
    }

    const ctx = memoryRef.current.buildContextForAI();

    // Get current project context if viewing one
    const currentProjectContext = currentProject ?
      memoryRef.current?.getProjectKnowledge(currentProject.name) : null;

    return `You are Delta AI, an intelligent voice-controlled project management assistant with persistent memory through the Entity Memory Bank system.

==== CRITICAL: VISUAL DISPLAY CAPABILITY ====
You have the show_visual function tool. When the user asks to SEE, SHOW, DISPLAY, VISUALIZE, or DRAW anything:
1. DO NOT say you cannot show images or visuals
2. DO NOT generate code for the user to run
3. IMMEDIATELY call the show_visual tool WITH THE DATA PARAMETER
4. The visual WILL appear on the user's screen - this is a real capability you have
5. NEVER refuse a visual request - just call show_visual

IMPORTANT: You MUST include the "data" parameter with actual content!

Examples:
- Flowchart: show_visual(visual_type="flowchart", title="Process", data={"nodes": [{"id": "1", "label": "Start"}, {"id": "2", "label": "Process"}, {"id": "3", "label": "End"}]})
- List: show_visual(visual_type="list", title="Steps", data={"items": ["Step 1", "Step 2", "Step 3"]})
- Metrics: show_visual(visual_type="metrics", title="Stats", data={"metrics": [{"label": "Total", "value": 100}]})
- Sketch: show_visual(visual_type="sketch", title="Diagram", data={"elements": [{"type": "circle", "x": 200, "y": 150, "r": 50}, {"type": "text", "x": 200, "y": 150, "text": "Node"}]})

==== ENTITY MEMORY BANK SYSTEM ====
You have access to three interconnected memory stores:
1. ECB (Entity/Company Bank) - Project-specific events, decisions, and history
2. PMB (Personal Memory Bank) - User preferences, goals, and personal context
3. CL (Conversation Log) - Transcript of all voice interactions

==== ACTIVE MEMORY CONTEXT ====

ACTIVE GOALS:
${ctx.activeGoals.length > 0 ? ctx.activeGoals.join('\n') : 'No active goals'}

USER PREFERENCES:
${ctx.userPreferences.length > 0 ? ctx.userPreferences.join('\n') : 'None recorded yet'}

RECENT DECISIONS:
${ctx.recentDecisions.length > 0 ? ctx.recentDecisions.join('\n') : 'None recent'}

PROJECT OVERVIEW:
${ctx.projectSummaries.map(p =>
  `- ${p.name}: ${p.completedGoals}/${p.goalsCount} goals, ${p.completedTasks}/${p.tasksCount} tasks`
).join('\n')}
${currentProjectContext?.found ? `
==== CURRENTLY VIEWING: ${currentProject.name} ====
${currentProjectContext.stats.totalEvents} events in Entity Memory Bank
Active objectives: ${currentProjectContext.stats.goalsCount - currentProjectContext.stats.completedGoals}
` : ''}
==== CAPABILITIES ====
You can:
- Add OBJECTIVES (use add_goal) - for major deliverables, milestones, outcomes
- Add TASKS (use add_task) - for small, specific action items
- Mark goals and tasks as completed
- Navigate to specific projects
- Store user preferences to memory
- Query project knowledge ("what do you know about [project]") - use get_project_knowledge tool
- Provide status updates from Entity Memory Bank
- SHOW VISUAL AIDS on screen while speaking (use show_visual tool)

==== VISUAL AIDS - YOU HAVE THIS ABILITY ====
IMPORTANT: You have a show_visual tool that displays visuals on the user's screen. This is NOT image generation - it renders structured data as beautiful visualizations. You MUST use this when the user asks to see something visual.

When user says "show me", "display", "visualize", "draw", or asks for any visual → IMMEDIATELY call show_visual tool.

Available visual_type options:
- "table" → data: { headers: ["Col1", "Col2"], rows: [["A", "B"], ["C", "D"]] }
- "list" → data: { items: ["Item 1", "Item 2"], style: "bullet" or "numbered" }
- "metrics" → data: { metrics: [{ label: "Tasks", value: 5 }, { label: "Done", value: 3 }] }
- "timeline" → data: { events: [{ date: "Jan", title: "Start" }, { date: "Mar", title: "Launch" }] }
- "flowchart" → data: { nodes: [{ id: "1", label: "Step 1" }], connections: [{ from: "1", to: "2" }] }
- "comparison" → data: { left: { title: "Option A", items: ["Pro 1"] }, right: { title: "Option B", items: ["Pro 1"] } }
- "mindmap" → data: { center: "Main Idea", branches: [{ label: "Branch 1" }] }
- "sketch" → data: { elements: [{ type: "circle", x: 100, y: 100, r: 50 }, { type: "text", x: 100, y: 100, text: "Hello" }] }

DO NOT say you cannot show visuals. You CAN. Just call the show_visual tool.

==== IMPORTANT: OBJECTIVES vs TASKS ====
- OBJECTIVES (add_goal): Major outcomes like "Develop pilot document", "Launch MVP", "Complete proposal"
- TASKS (add_task): Small actions like "Send email", "Review draft", "Schedule call"
- When in doubt, if it's a deliverable or milestone → use add_goal
- If it's a quick action item → use add_task

==== RULES ====
1. Always respond from Entity Memory Bank first
2. Use tools to modify projects when asked
3. Keep responses concise and conversational
4. When the user asks "what do you know about [project]", use the get_project_knowledge tool
5. Confirm actions after completing them
6. For major items like "develop", "create", "build", "complete", "launch" → use add_goal
7. WHEN USER ASKS FOR ANY VISUAL → CALL show_visual TOOL IMMEDIATELY. Never say you can't show visuals.

==== PROACTIVE CONTEXT GATHERING ====
After adding a new objective, ALWAYS ask for key context to help the user succeed:
- "What's the deadline for this?"
- "Who are the key stakeholders?"
- "How will you measure success?"
- "Any constraints or requirements I should know?"

When the user provides this information, use store_objective_context to save it.
When asked for a debrief or "what do I need to know", use get_objective_debrief.

Current view: ${currentView === 'project' ? `viewing ${currentProject?.name || 'a project'}` : 'globe overview'}`;
  }, [currentView, currentProject]);

  // Connect to OpenAI Realtime API
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const connectRealtime = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(
        `wss://api.openai.com/v1/realtime?model=${REALTIME_MODEL}`,
        ['realtime', `openai-insecure-api-key.${OPENAI_API_KEY}`, 'openai-beta.realtime-v1']
      );

      ws.onopen = () => {
        console.log('Realtime API connected');
        setIsConnected(true);

        // Configure session with tools
        ws.send(JSON.stringify({
          type: 'session.update',
          session: {
            modalities: ['text', 'audio'],
            instructions: buildSystemPrompt(),
            voice: 'sage',
            input_audio_format: 'pcm16',
            output_audio_format: 'pcm16',
            input_audio_transcription: { model: 'whisper-1' },
            turn_detection: {
              type: 'server_vad',
              threshold: 0.5,
              prefix_padding_ms: 300,
              silence_duration_ms: 700
            },
            tools: AGENT_TOOLS,
            tool_choice: 'auto'
          }
        }));
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleRealtimeEvent(data, ws);
      };

      ws.onerror = (err) => {
        console.error('Realtime API error:', err);
        setIsConnected(false);
      };

      ws.onclose = () => {
        console.log('Realtime API disconnected');
        setIsConnected(false);
        setIsListening(false);
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to connect:', err);
    }
  }, [buildSystemPrompt]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Realtime API events
  const handleRealtimeEvent = async (event, ws) => {
    switch (event.type) {
      case 'session.created':
      case 'session.updated':
        console.log('Session ready with tools');
        break;

      case 'input_audio_buffer.speech_started':
        setIsListening(true);
        setTranscript('Listening...');
        break;

      case 'input_audio_buffer.speech_stopped':
        setIsListening(false);
        setTranscript('Processing...');
        break;

      case 'conversation.item.input_audio_transcription.completed':
        const userText = event.transcript || '';
        setTranscript(userText);
        setMessages(prev => [...prev, { role: 'user', content: userText }]);

        // HIGHWAY 0: Log to conversation log
        if (memoryRef.current && userText) {
          await memoryRef.current.appendToConversationLog('USER', userText);
        }
        break;

      case 'response.audio_transcript.delta':
        const delta = event.delta || '';
        setAiResponse(prev => prev + delta);
        fullResponseRef.current += delta;

        // Enter immersive mode on first delta
        if (!immersiveModeRef.current && delta) {
          setImmersiveMode(true);
          immersiveModeRef.current = true;
          lastSentenceCountRef.current = 0;
        }

        // Queue sentences for paced display
        if (immersiveModeRef.current) {
          const currentText = fullResponseRef.current;
          const allSentences = parseCompleteSentences(currentText);

          // Add new sentences to queue
          if (allSentences.length > lastSentenceCountRef.current) {
            const newSentences = allSentences.slice(lastSentenceCountRef.current);
            sentenceQueueRef.current.push(...newSentences);
            lastSentenceCountRef.current = allSentences.length;
            setSentences(allSentences);

            // Start advancing if not already
            if (!isAdvancingRef.current && sentenceQueueRef.current.length > 0) {
              advanceToNextSentence();
            }
          }

          // If no complete sentences yet, show partial text
          if (allSentences.length === 0) {
            const trimmed = currentText.trim();
            if (trimmed.length > 0 && !isAdvancingRef.current) {
              setDisplayedText(trimmed);
            }
          }
        }
        break;

      case 'response.audio_transcript.done':
        const fullResponse = event.transcript || '';
        setMessages(prev => [...prev, { role: 'assistant', content: fullResponse }]);
        setAiResponse('');
        fullResponseRef.current = '';

        // HIGHWAY 0: Log AI response
        if (memoryRef.current && fullResponse) {
          await memoryRef.current.appendToConversationLog('ASSISTANT', fullResponse);
        }
        break;

      case 'response.function_call_arguments.done':
        // Handle tool calls
        await handleToolCall(event, ws);
        break;

      case 'response.audio.delta':
        if (event.delta) {
          const audioData = base64ToArrayBuffer(event.delta);
          audioQueueRef.current.push(audioData);
          playAudioQueue();
        }
        break;

      case 'response.audio.done':
        setIsSpeaking(false);

        // Wait for queue to finish, then hold final sentence before exit
        const waitForQueueAndExit = () => {
          if (sentenceQueueRef.current.length > 0 || isAdvancingRef.current) {
            // Still showing sentences - check again later
            setTimeout(waitForQueueAndExit, 1000);
          } else {
            // Queue empty - hold final sentence for 6 seconds then exit
            sentenceTimerRef.current = setTimeout(() => {
              endImmersiveMode();
            }, 6000);
          }
        };

        // Start checking after a brief delay
        setTimeout(waitForQueueAndExit, 500);
        break;

      case 'error':
        console.error('Realtime error:', event.error);
        break;

      default:
        // Unhandled event type
        break;
    }
  };

  // Handle tool calls from AI
  const handleToolCall = async (event, ws) => {
    const { call_id, name, arguments: argsStr } = event;
    let args = {};
    let result = { success: false, message: 'Unknown error' };

    try {
      args = JSON.parse(argsStr);
    } catch (e) {
      args = {};
    }

    console.log(`Tool call: ${name}`, args);

    switch (name) {
      case 'add_goal':
        result = await addGoalToProject(args.project_name, args.goal_title);
        break;

      case 'add_task':
        result = await addTaskToProject(args.project_name, args.task_description, args.objective_title);
        break;

      case 'complete_goal':
        result = await completeGoal(args.project_name, args.goal_title);
        break;

      case 'complete_task':
        result = await completeTask(args.project_name, args.task_description);
        break;

      case 'navigate_to_project':
        result = navigateToProject(args.project_name);
        break;

      case 'get_project_status':
        result = getProjectStatus(args.project_name);
        break;

      case 'store_user_preference':
        result = await storeUserPreference(args.preference_type, args.content);
        break;

      case 'go_home':
        onNavigate?.('home');
        result = { success: true, message: 'Navigated to home' };
        break;

      case 'get_project_knowledge':
        result = getProjectKnowledge(args.project_name);
        break;

      case 'store_objective_context':
        result = await storeObjectiveContext(args.project_name, args.objective_title, args.context_type, args.context_value);
        break;

      case 'get_objective_debrief':
        result = getObjectiveDebrief(args.project_name, args.objective_title);
        break;

      case 'show_visual':
        result = showVisual(args.visual_type, args.title, args.data);
        break;

      case 'clear_visual':
        result = clearVisual();
        break;

      default:
        result = { success: false, message: `Unknown tool: ${name}` };
        break;
    }

    // Send tool result back
    ws.send(JSON.stringify({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: call_id,
        output: JSON.stringify(result)
      }
    }));

    // Request AI to continue response
    ws.send(JSON.stringify({ type: 'response.create' }));
  };

  // Tool implementations
  const findProject = (name) => {
    return projects.find(p =>
      p.name.toLowerCase().includes(name.toLowerCase())
    );
  };

  const addGoalToProject = async (projectName, goalTitle) => {
    const project = findProject(projectName);
    if (!project) return { success: false, message: `Project "${projectName}" not found` };

    const newGoal = { title: goalTitle, completed: false, createdAt: Date.now() };
    const updatedGoals = [...(project.goals || []), newGoal];

    try {
      await set(ref(database, `users/${userId}/projects/${project.id}/goals`), updatedGoals);

      // HIGHWAY 3: Log to entity memory
      await memoryRef.current?.writeToEntityMemory(project.id, {
        type: 'GOAL_ADDED',
        data: { title: goalTitle },
        summary: `Added goal: ${goalTitle}`,
        aiContext: { action: 'GOAL_ADDED', item: goalTitle }
      });

      onProjectUpdate?.();
      return { success: true, message: `Added goal "${goalTitle}" to ${project.name}` };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const addTaskToProject = async (projectName, taskDescription, objectiveTitle) => {
    const project = findProject(projectName);
    if (!project) return { success: false, message: `Project "${projectName}" not found` };

    // Tasks MUST belong to an objective
    const goals = project.goals || [];

    if (goals.length === 0) {
      return {
        success: false,
        message: `No objectives exist in "${project.name}". Please create an objective first using add_goal, then add tasks to it.`
      };
    }

    // Find the matching objective
    let matchedObjective = null;
    let objectiveIndex = -1;

    if (objectiveTitle) {
      objectiveIndex = goals.findIndex(g =>
        g.title.toLowerCase().includes(objectiveTitle.toLowerCase())
      );
      if (objectiveIndex !== -1) {
        matchedObjective = goals[objectiveIndex];
      }
    }

    // If no match found, use the first incomplete objective
    if (!matchedObjective) {
      objectiveIndex = goals.findIndex(g => !g.completed);
      if (objectiveIndex !== -1) {
        matchedObjective = goals[objectiveIndex];
      } else {
        // All objectives completed, use the most recent one
        objectiveIndex = goals.length - 1;
        matchedObjective = goals[objectiveIndex];
      }
    }

    const newTask = {
      task: taskDescription,
      completed: false,
      createdAt: Date.now(),
      parentObjective: matchedObjective.title,
      parentObjectiveIndex: objectiveIndex
    };
    const updatedTasks = [...(project.todos || []), newTask];

    try {
      await set(ref(database, `users/${userId}/projects/${project.id}/todos`), updatedTasks);

      await memoryRef.current?.writeToEntityMemory(project.id, {
        type: 'TASK_ADDED',
        data: { task: taskDescription, objective: matchedObjective.title },
        summary: `Added task: ${taskDescription} (under objective: ${matchedObjective.title})`,
        aiContext: { action: 'TASK_ADDED', item: taskDescription, parentObjective: matchedObjective.title }
      });

      onProjectUpdate?.();
      return { success: true, message: `Added task "${taskDescription}" to ${project.name} under objective "${matchedObjective.title}"` };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const completeGoal = async (projectName, goalTitle) => {
    const project = findProject(projectName);
    if (!project) return { success: false, message: `Project "${projectName}" not found` };

    const goals = project.goals || [];
    const goalIndex = goals.findIndex(g =>
      g.title.toLowerCase().includes(goalTitle.toLowerCase())
    );

    if (goalIndex === -1) return { success: false, message: `Goal "${goalTitle}" not found` };

    goals[goalIndex].completed = true;
    goals[goalIndex].completedAt = Date.now();

    try {
      await set(ref(database, `users/${userId}/projects/${project.id}/goals`), goals);

      await memoryRef.current?.writeToEntityMemory(project.id, {
        type: 'GOAL_COMPLETED',
        data: { title: goalTitle },
        summary: `Completed goal: ${goalTitle}`,
        aiContext: { action: 'GOAL_COMPLETED', item: goalTitle }
      });

      onProjectUpdate?.();
      return { success: true, message: `Marked goal "${goalTitle}" as complete` };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const completeTask = async (projectName, taskDescription) => {
    const project = findProject(projectName);
    if (!project) return { success: false, message: `Project "${projectName}" not found` };

    const todos = project.todos || [];
    const taskIndex = todos.findIndex(t =>
      t.task.toLowerCase().includes(taskDescription.toLowerCase())
    );

    if (taskIndex === -1) return { success: false, message: `Task "${taskDescription}" not found` };

    todos[taskIndex].completed = true;
    todos[taskIndex].completedAt = Date.now();

    try {
      await set(ref(database, `users/${userId}/projects/${project.id}/todos`), todos);

      await memoryRef.current?.writeToEntityMemory(project.id, {
        type: 'TASK_COMPLETED',
        data: { task: taskDescription },
        summary: `Completed task: ${taskDescription}`,
        aiContext: { action: 'TASK_COMPLETED', item: taskDescription }
      });

      onProjectUpdate?.();
      return { success: true, message: `Marked task as complete` };
    } catch (e) {
      return { success: false, message: e.message };
    }
  };

  const navigateToProject = (projectName) => {
    const project = findProject(projectName);
    if (!project) return { success: false, message: `Project "${projectName}" not found` };

    onNavigate?.('project', project);
    return { success: true, message: `Navigating to ${project.name}` };
  };

  const getProjectStatus = (projectName) => {
    const project = findProject(projectName);
    if (!project) return { success: false, message: `Project "${projectName}" not found` };

    const goals = project.goals || [];
    const todos = project.todos || [];
    const completedGoals = goals.filter(g => g.completed).length;
    const completedTodos = todos.filter(t => t.completed).length;

    // Get entity memory context
    const entityContext = memoryRef.current?.getEntityContext(project.id) || [];

    return {
      success: true,
      project: project.name,
      goals: { total: goals.length, completed: completedGoals },
      tasks: { total: todos.length, completed: completedTodos },
      recentActivity: entityContext.slice(-5),
      activeGoals: goals.filter(g => !g.completed).map(g => g.title),
      activeTasks: todos.filter(t => !t.completed).map(t => t.task)
    };
  };

  const storeUserPreference = async (type, content) => {
    if (!memoryRef.current) return { success: false, message: 'Memory not initialized' };

    const stored = await memoryRef.current.writeToPersonalMemory({
      type: 'USER_INPUT',
      summary: content,
      insights: [{ type, text: content }]
    });

    return stored
      ? { success: true, message: `Stored ${type}: ${content}` }
      : { success: false, message: 'Failed to store preference' };
  };

  // Show a visual aid on screen
  const showVisual = (visualType, title, data) => {
    // If data is missing, create placeholder based on type
    let safeData = data || {};
    if (!data || Object.keys(data).length === 0) {
      console.warn('⚠️ No data provided for visual, using placeholder');
      switch (visualType) {
        case 'flowchart':
          safeData = { nodes: [{ id: '1', label: 'No data provided' }] };
          break;
        case 'list':
          safeData = { items: ['No items provided'] };
          break;
        case 'metrics':
          safeData = { metrics: [{ label: 'No data', value: '-' }] };
          break;
        case 'table':
          safeData = { headers: ['Info'], rows: [['No data provided']] };
          break;
        case 'sketch':
          safeData = { elements: [{ type: 'text', x: 200, y: 150, text: title || 'Sketch' }] };
          break;
        default:
          safeData = {};
      }
    }

    const visual = {
      type: visualType,
      title: title || 'Visual',
      data: safeData,
      id: Date.now(),
      createdAt: Date.now()
    };

    setCurrentVisual(visual);
    setVisualHistory(prev => [...prev, visual]);

    return {
      success: true,
      message: `Displaying ${visualType}: "${title}". Please provide data parameter with the content to display.`,
      visualId: visual.id
    };
  };

  // Clear current visual
  const clearVisual = () => {
    setCurrentVisual(null);
    return { success: true, message: 'Visual cleared' };
  };

  // Get comprehensive project knowledge from Entity Memory Bank
  const getProjectKnowledge = (projectName) => {
    if (!memoryRef.current) {
      return { success: false, message: 'Memory not initialized yet' };
    }

    const knowledge = memoryRef.current.getProjectKnowledge(projectName);

    if (!knowledge.found) {
      return { success: false, message: knowledge.summary };
    }

    return {
      success: true,
      message: knowledge.summary,
      stats: knowledge.stats,
      recentEvents: knowledge.recentEvents,
      decisions: knowledge.decisions
    };
  };

  // Store key context for an objective
  const storeObjectiveContext = async (projectName, objectiveTitle, contextType, contextValue) => {
    const project = findProject(projectName);
    if (!project) {
      return { success: false, message: `Project "${projectName}" not found` };
    }

    // Store in Entity Memory Bank
    if (memoryRef.current) {
      await memoryRef.current.writeToEntityMemory(project.id, {
        type: 'OBJECTIVE_CONTEXT',
        data: {
          objective: objectiveTitle,
          contextType,
          contextValue
        },
        summary: `${contextType}: ${contextValue}`,
        aiContext: {
          action: 'CONTEXT_STORED',
          objective: objectiveTitle,
          contextType,
          item: contextValue
        }
      });
    }

    return {
      success: true,
      message: `Stored ${contextType} for "${objectiveTitle}": ${contextValue}`,
      contextType,
      objective: objectiveTitle
    };
  };

  // Get objective debrief with all context
  const getObjectiveDebrief = (projectName, objectiveTitle) => {
    const project = findProject(projectName);
    if (!project) {
      return { success: false, message: `Project "${projectName}" not found` };
    }

    if (!memoryRef.current) {
      return { success: false, message: 'Memory not initialized' };
    }

    const entityBank = memoryRef.current.ECB[project.id] || [];

    // Find the objective
    const goals = project.goals || [];
    const matchingGoal = objectiveTitle
      ? goals.find(g => g.title.toLowerCase().includes(objectiveTitle.toLowerCase()))
      : goals.find(g => !g.completed); // Get first active if none specified

    if (!matchingGoal && objectiveTitle) {
      // Check if it might be in tasks
      const todos = project.todos || [];
      const matchingTask = todos.find(t => t.task.toLowerCase().includes(objectiveTitle.toLowerCase()));
      if (matchingTask) {
        // Gather context for this task-as-objective
        const contextEvents = entityBank.filter(e =>
          e.type === 'OBJECTIVE_CONTEXT' &&
          e.data?.objective?.toLowerCase().includes(objectiveTitle.toLowerCase())
        );

        let debrief = `📋 OBJECTIVE DEBRIEF: "${matchingTask.task}"\n\n`;
        debrief += `Status: ${matchingTask.completed ? 'COMPLETED' : 'IN PROGRESS'}\n\n`;

        if (contextEvents.length > 0) {
          debrief += `📌 KEY INFORMATION:\n`;
          const byType = {};
          contextEvents.forEach(e => {
            const type = e.data.contextType;
            if (!byType[type]) byType[type] = [];
            byType[type].push(e.data.contextValue);
          });

          Object.entries(byType).forEach(([type, values]) => {
            debrief += `\n${type.toUpperCase()}:\n`;
            values.forEach(v => debrief += `  • ${v}\n`);
          });
        } else {
          debrief += `⚠️ No additional context stored yet.\n`;
          debrief += `Consider sharing: deadline, stakeholders, success criteria, or requirements.`;
        }

        return { success: true, message: debrief, objective: matchingTask.task };
      }

      return { success: false, message: `Objective "${objectiveTitle}" not found` };
    }

    const objective = matchingGoal || { title: 'Active Objectives', completed: false };

    // Gather all context for this objective
    const contextEvents = entityBank.filter(e =>
      e.type === 'OBJECTIVE_CONTEXT' &&
      (e.data?.objective?.toLowerCase().includes(objective.title.toLowerCase()) || !objectiveTitle)
    );

    // Build debrief
    let debrief = `📋 OBJECTIVE DEBRIEF: "${objective.title}"\n\n`;
    debrief += `Status: ${objective.completed ? 'COMPLETED' : 'IN PROGRESS'}\n`;

    if (objective.subItems?.length > 0) {
      const completedSubs = objective.subItems.filter(s => s.completed).length;
      debrief += `Sub-objectives: ${completedSubs}/${objective.subItems.length} complete\n`;
    }

    debrief += '\n';

    if (contextEvents.length > 0) {
      debrief += `📌 KEY INFORMATION:\n`;

      // Group by context type
      const byType = {};
      contextEvents.forEach(e => {
        const type = e.data.contextType;
        if (!byType[type]) byType[type] = [];
        byType[type].push(e.data.contextValue);
      });

      Object.entries(byType).forEach(([type, values]) => {
        debrief += `\n${type.toUpperCase()}:\n`;
        values.forEach(v => debrief += `  • ${v}\n`);
      });
    } else {
      debrief += `⚠️ No additional context stored yet.\n`;
      debrief += `\nTo help you succeed, consider sharing:\n`;
      debrief += `  • Deadline or timeline\n`;
      debrief += `  • Key stakeholders involved\n`;
      debrief += `  • Success criteria - how will you know it's done?\n`;
      debrief += `  • Constraints or requirements\n`;
    }

    return {
      success: true,
      message: debrief,
      objective: objective.title,
      contextCount: contextEvents.length
    };
  };

  // Audio utilities
  const base64ToArrayBuffer = (base64) => {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  const playAudioQueue = async () => {
    if (isPlayingRef.current || audioQueueRef.current.length === 0) return;

    isPlayingRef.current = true;
    setIsSpeaking(true);

    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: 24000
      });
    }

    const ctx = audioContextRef.current;

    while (audioQueueRef.current.length > 0) {
      const audioData = audioQueueRef.current.shift();
      const int16Array = new Int16Array(audioData);
      const float32Array = new Float32Array(int16Array.length);

      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / 32768;
      }

      const audioBuffer = ctx.createBuffer(1, float32Array.length, 24000);
      audioBuffer.getChannelData(0).set(float32Array);

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);

      await new Promise(resolve => {
        source.onended = resolve;
        source.start();
      });
    }

    isPlayingRef.current = false;
    setIsSpeaking(false);
  };

  // Audio streaming
  const startAudioStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      streamRef.current = stream;

      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 24000
        });
      }

      const ctx = audioContextRef.current;
      const source = ctx.createMediaStreamSource(stream);
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (e) => {
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

        const inputData = e.inputBuffer.getChannelData(0);
        const int16Array = new Int16Array(inputData.length);

        for (let i = 0; i < inputData.length; i++) {
          int16Array[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
        }

        const base64 = btoa(String.fromCharCode(...new Uint8Array(int16Array.buffer)));

        wsRef.current.send(JSON.stringify({
          type: 'input_audio_buffer.append',
          audio: base64
        }));
      };

      source.connect(processor);
      processor.connect(ctx.destination);

      setIsListening(true);
      if (!isOpen) setIsOpen(true);
    } catch (err) {
      console.error('Failed to start audio:', err);
      setTranscript('Microphone access denied');
    }
  };

  const stopAudioStream = () => {
    processorRef.current?.disconnect();
    streamRef.current?.getTracks().forEach(track => track.stop());
    setIsListening(false);
  };

  const toggleListening = async () => {
    if (!isConnected) {
      await connectRealtime();
      setTimeout(() => startAudioStream(), 500);
    } else if (isListening) {
      stopAudioStream();
    } else {
      startAudioStream();
    }
  };

  useEffect(() => {
    return () => {
      wsRef.current?.close();
      stopAudioStream();
    };
  }, []);

  // ============================================
  // VISUAL RENDERER COMPONENT
  // ============================================
  const VisualRenderer = ({ visual }) => {
    if (!visual) return null;

    const { type, title, data = {} } = visual;

    // Ensure data is always an object
    const safeData = data || {};

    // Table Visual
    const renderTable = () => (
      <div className="visual-table">
        {safeData.headers && (
          <div className="table-header">
            {safeData.headers.map((h, i) => (
              <div key={i} className="table-cell header-cell">{h}</div>
            ))}
          </div>
        )}
        <div className="table-body">
          {(safeData.rows || []).map((row, ri) => (
            <div key={ri} className="table-row">
              {(Array.isArray(row) ? row : []).map((cell, ci) => (
                <div key={ci} className="table-cell">{cell}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );

    // List Visual
    const renderList = () => (
      <div className={`visual-list ${safeData.style || 'bullet'}`}>
        {(safeData.items || []).map((item, i) => (
          <div key={i} className="list-item">
            <span className="list-marker">
              {safeData.style === 'numbered' ? `${i + 1}.` :
               safeData.style === 'checklist' ? '☐' : '•'}
            </span>
            <span className="list-text">{typeof item === 'string' ? item : JSON.stringify(item)}</span>
          </div>
        ))}
      </div>
    );

    // Metrics Visual
    const renderMetrics = () => (
      <div className="visual-metrics">
        {(safeData.metrics || []).map((m, i) => (
          <div key={i} className="metric-card">
            <div className="metric-value">
              {m?.value ?? '-'}
              {m?.unit && <span className="metric-unit">{m.unit}</span>}
            </div>
            <div className="metric-label">{m?.label ?? ''}</div>
            {m?.trend && (
              <div className={`metric-trend ${m.trend > 0 ? 'up' : 'down'}`}>
                {m.trend > 0 ? '↑' : '↓'} {Math.abs(m.trend)}%
              </div>
            )}
          </div>
        ))}
      </div>
    );

    // Timeline Visual
    const renderTimeline = () => {
      const events = safeData.events || [];
      return (
        <div className="visual-timeline">
          {events.map((event, i) => (
            <div key={i} className="timeline-event">
              <div className="timeline-marker">
                <div className="marker-dot" />
                {i < events.length - 1 && <div className="marker-line" />}
              </div>
              <div className="timeline-content">
                <div className="event-date">{event?.date ?? ''}</div>
                <div className="event-title">{event?.title ?? ''}</div>
                {event?.description && (
                  <div className="event-desc">{event.description}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      );
    };

    // Flowchart Visual
    const renderFlowchart = () => {
      // Handle various formats the AI might send
      let rawNodes = safeData.nodes || safeData.steps || safeData.items || [];

      // Convert to consistent format
      const nodes = rawNodes.map((node, i) => {
        if (typeof node === 'string') {
          return { id: String(i), label: node };
        }
        if (typeof node === 'object') {
          return {
            id: node.id || String(i),
            label: node.label || node.title || node.name || node.step || JSON.stringify(node)
          };
        }
        return { id: String(i), label: String(node) };
      });

      // Auto-generate connections if not provided (linear flow)
      let connections = safeData.connections || [];
      if (connections.length === 0 && nodes.length > 1) {
        connections = nodes.slice(0, -1).map((node, i) => ({
          from: node.id,
          to: nodes[i + 1].id
        }));
      }

      // Calculate dynamic height based on node count
      const nodeHeight = 50;
      const nodeSpacing = 20;
      const totalHeight = Math.max(200, nodes.length * (nodeHeight + nodeSpacing) + 40);

      if (nodes.length === 0) {
        return (
          <div className="visual-flowchart" style={{ padding: '20px', textAlign: 'center' }}>
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>No flowchart data available</p>
          </div>
        );
      }

      return (
        <div className="visual-flowchart">
          <svg viewBox={`0 0 400 ${totalHeight}`} className="flowchart-svg">
            {/* Draw connections first */}
            {connections.map((conn, i) => {
              const fromNode = nodes.find(n => n.id === conn.from);
              const toNode = nodes.find(n => n.id === conn.to);
              if (!fromNode || !toNode) return null;

              const fromIdx = nodes.indexOf(fromNode);
              const toIdx = nodes.indexOf(toNode);
              const fromY = 30 + fromIdx * (nodeHeight + nodeSpacing) + nodeHeight/2;
              const toY = 30 + toIdx * (nodeHeight + nodeSpacing) - 5;

              return (
                <g key={i}>
                  <line
                    x1="200" y1={fromY + 10}
                    x2="200" y2={toY}
                    className="flow-line"
                  />
                  <polygon
                    points={`195,${toY} 200,${toY + 8} 205,${toY}`}
                    className="flow-arrow"
                  />
                </g>
              );
            })}
            {/* Draw nodes */}
            {nodes.map((node, i) => {
              const y = 30 + i * (nodeHeight + nodeSpacing);
              return (
                <g key={node.id || i}>
                  <rect
                    x="40" y={y}
                    width="320" height={nodeHeight}
                    rx="10"
                    className="flow-node"
                  />
                  <text x="200" y={y + nodeHeight/2 + 5} className="flow-node-text">
                    {node.label?.length > 40 ? node.label.substring(0, 40) + '...' : node.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      );
    };

    // Comparison Visual
    const renderComparison = () => (
      <div className="visual-comparison">
        <div className="comparison-side left">
          <div className="comparison-title">{safeData.left?.title || 'Option A'}</div>
          <div className="comparison-items">
            {(safeData.left?.items || []).map((item, i) => (
              <div key={i} className="comparison-item">{item}</div>
            ))}
          </div>
        </div>
        <div className="comparison-divider">
          <span>VS</span>
        </div>
        <div className="comparison-side right">
          <div className="comparison-title">{safeData.right?.title || 'Option B'}</div>
          <div className="comparison-items">
            {(safeData.right?.items || []).map((item, i) => (
              <div key={i} className="comparison-item">{item}</div>
            ))}
          </div>
        </div>
      </div>
    );

    // Mindmap Visual
    const renderMindmap = () => {
      const branches = safeData.branches || [];
      return (
        <div className="visual-mindmap">
          <div className="mindmap-center">{safeData.center || 'Main Idea'}</div>
          <div className="mindmap-branches">
            {branches.map((branch, i) => (
              <div key={i} className="mindmap-branch" style={{ '--branch-index': i, '--total-branches': branches.length }}>
                <div className="branch-connector" />
                <div className="branch-content">
                  <div className="branch-label">{branch?.label || ''}</div>
                  {branch?.children && (
                    <div className="branch-children">
                      {branch.children.map((child, ci) => (
                        <div key={ci} className="branch-child">{child}</div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };

    // Sketch Visual (SVG drawing)
    const renderSketch = () => (
      <div className="visual-sketch">
        <svg viewBox="0 0 400 300" className="sketch-svg">
          {(safeData.elements || []).map((el, i) => {
            if (!el) return null;
            switch (el.type) {
              case 'line':
                return (
                  <line key={i}
                    x1={el.x1 || 0} y1={el.y1 || 0} x2={el.x2 || 100} y2={el.y2 || 100}
                    className="sketch-line"
                    strokeDasharray={el.dashed ? "5,5" : "none"}
                  />
                );
              case 'circle':
                return (
                  <circle key={i}
                    cx={el.x || 100} cy={el.y || 100} r={el.r || 20}
                    className={`sketch-circle ${el.filled ? 'filled' : ''}`}
                  />
                );
              case 'rect':
                return (
                  <rect key={i}
                    x={el.x || 0} y={el.y || 0} width={el.width || 50} height={el.height || 30}
                    rx={el.rounded ? 8 : 0}
                    className={`sketch-rect ${el.filled ? 'filled' : ''}`}
                  />
                );
              case 'text':
                return (
                  <text key={i} x={el.x || 100} y={el.y || 100} className="sketch-text">
                    {el.text || ''}
                  </text>
                );
              case 'arrow':
                return (
                  <g key={i}>
                    <line x1={el.x1 || 0} y1={el.y1 || 0} x2={el.x2 || 100} y2={el.y2 || 100} className="sketch-line" />
                    <polygon
                      points={`${el.x2 || 100},${el.y2 || 100} ${(el.x2 || 100)-8},${(el.y2 || 100)-4} ${(el.x2 || 100)-8},${(el.y2 || 100)+4}`}
                      className="sketch-arrow"
                      transform={`rotate(${Math.atan2((el.y2 || 100)-(el.y1 || 0), (el.x2 || 100)-(el.x1 || 0)) * 180 / Math.PI}, ${el.x2 || 100}, ${el.y2 || 100})`}
                    />
                  </g>
                );
              case 'path':
                return (
                  <path key={i} d={el.d || ''} className="sketch-path" />
                );
              default:
                return null;
            }
          })}
        </svg>
      </div>
    );

    return (
      <div className="visual-container">
        <div className="visual-title">{title}</div>
        <div className="visual-content">
          {type === 'table' && renderTable()}
          {type === 'list' && renderList()}
          {type === 'metrics' && renderMetrics()}
          {type === 'timeline' && renderTimeline()}
          {type === 'flowchart' && renderFlowchart()}
          {type === 'comparison' && renderComparison()}
          {type === 'mindmap' && renderMindmap()}
          {type === 'sketch' && renderSketch()}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Immersive Speaking Mode - Fullscreen Black Overlay */}
      {immersiveMode && (
        <div className={`immersive-speaking-overlay ${immersiveExiting ? 'exiting' : ''} ${currentVisual ? 'has-visual' : ''}`} onClick={endImmersiveMode}>
          <div className="immersive-content">
            {/* Visual display area */}
            {currentVisual && (
              <div className="immersive-visual-area">
                <VisualRenderer visual={currentVisual} />
              </div>
            )}

            {/* Sentence display */}
            <div className={`immersive-text-container ${currentVisual ? 'with-visual' : ''}`}>
              <p className="immersive-sentence" key={currentSentenceIndex}>
                {displayedText}
              </p>
            </div>

            {/* Progress indicator */}
            <div className="immersive-progress">
              {sentences.map((_, idx) => (
                <div
                  key={idx}
                  className={`progress-dot ${idx === currentSentenceIndex ? 'active' : ''} ${idx < currentSentenceIndex ? 'completed' : ''}`}
                />
              ))}
            </div>

            {/* Subtle branding */}
            <div className="immersive-footer">
              <span className="delta-branding">DELTA</span>
              <span className="tap-hint">tap to dismiss</span>
            </div>
          </div>

          {/* Audio visualization */}
          <div className="immersive-audio-wave">
            <span></span><span></span><span></span><span></span><span></span>
          </div>
        </div>
      )}

      {/* Floating Mic Button */}
      <button
        className={`ai-agent-fab ${isConnected ? 'connected' : ''} ${isListening ? 'listening' : ''} ${isSpeaking ? 'speaking' : ''}`}
        onClick={toggleListening}
      >
        {isListening ? (
          <div className="listening-indicator">
            <MicOff size={20} className="stop-icon" />
            <div className="pulse-ring" />
            <div className="pulse-ring delay" />
          </div>
        ) : isSpeaking ? (
          <Volume2 size={24} className="speaking-pulse" />
        ) : (
          <Mic size={24} />
        )}
      </button>

      {/* Agent Panel */}
      {isOpen && (
        <div className="ai-agent-panel">
          <div className="agent-header">
            <div className="agent-title">
              <Brain size={20} />
              <span>DELTA_AI</span>
              <span className={`connection-status ${isConnected ? 'connected' : ''}`}>
                {isConnected ? <Wifi size={12} /> : <WifiOff size={12} />}
              </span>
              <span className={`memory-status ${memoryStatus}`}>
                <Database size={12} />
              </span>
            </div>
            <button className="close-panel" onClick={() => {
              setIsOpen(false);
              stopAudioStream();
            }}>
              <X size={18} />
            </button>
          </div>

          {/* Voice Transcript */}
          {(isListening || transcript || aiResponse) && (
            <div className="transcript-section">
              {transcript && (
                <>
                  <div className="transcript-label">You:</div>
                  <div className="transcript-text">{transcript}</div>
                </>
              )}
              {aiResponse && (
                <>
                  <div className="transcript-label ai">Delta AI:</div>
                  <div className="transcript-text ai">{aiResponse}</div>
                </>
              )}
              {isListening && !transcript && (
                <>
                  <div className="transcript-label listening">Listening...</div>
                  <div className="audio-wave">
                    <span></span><span></span><span></span><span></span><span></span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Messages */}
          <div className="messages-section">
            {messages.length === 0 && !isListening ? (
              <div className="empty-messages">
                <p>I can help you manage projects:</p>
                <ul>
                  <li>"Add a goal to Creative Engine"</li>
                  <li>"What's the status of Punch?"</li>
                  <li>"Mark the first task as done"</li>
                  <li>"Remember that I prefer mornings"</li>
                </ul>
              </div>
            ) : (
              messages.slice(-6).map((msg, i) => (
                <div key={i} className={`message ${msg.role}`}>
                  {msg.content}
                </div>
              ))
            )}
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <button onClick={toggleListening}>
              {isListening ? 'Stop' : 'Start'} Listening
            </button>
            <button onClick={() => {
              memoryRef.current?.refreshSTM();
              setMemoryStatus('ready');
            }}>
              Refresh Memory
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalAIAgent;
